import BuildConfig from '$extensionSrc/buildConfig'
import { XSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { TWITTER_AUTOMATION_STAGE_STATUSES } from './twitterAutomationStageStatuses'
import * as twitterUrls from './twitterUrls'
import { NO_TIMEOUT } from '../../consts/stageMachineConsts'
import { StageType, TwitterRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import { throwIfGlobalInterruptRequested } from '../globalInterrupt'
import { wait } from '../interruptiblePromises'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import {
  getElement,
  getOptionalElement,
  getInitialValueBySettingKey,
} from '../utils'
import { evaluateXpath } from '../xpath'

export const getCurrentValueFn = () => () => ''
export const getIsFulfilledFn = () => () => true
export const getCantUndoFn = () => defaultCantUndoFn
export const getIsDisabledFn = () => defaultIsDisabledFn
export const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

export type XLeaveDirectMessageState = {
  count: number // total count of dms left
  message: string
  result?: {
    title: string
    detail?: string
  }
  stats: {
    numConversationsLeft: number // in latest update
  }
}

const StageKey = {
  UPDATE_DELETE_MESSAGES: 'UPDATE_DELETE_MESSAGES',
}

async function waitForMessageSearchBarToLoad(
  messageList: HTMLElement,
): Promise<void> {
  await getOptionalElement<HTMLElement>(
    messageList,
    '//input[@placeholder="Search Direct Messages"]',
  )
}

async function getAllVisibleDmMenus(
  messageList: HTMLElement,
): Promise<HTMLElement[]> {
  await waitForMessageSearchBarToLoad(messageList) // indicator of page loaded
  return evaluateXpath<HTMLElement>(messageList, '//button[@aria-label="More"]')
}

// No gather or undo stages
export const gatherStageDefs: StageDef[] = []
export const undoStageDefs: StageDef[] = []

export const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_DELETE_MESSAGES,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.LEAVING_DIRECT_MESSAGES,
    url: twitterUrls.messagesUrl,
    timeout: NO_TIMEOUT,
    action: async (_machineContext, userContext) => {
      const {
        incrementTotalCount,
        initialValues: storedInitialValues,
        isDry,
        updateStoredInitialValues,
      } = userContext

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        XSettingKey.LEAVE_DIRECT_MESSAGES,
      )

      let totalDirectMessagesLeft = initialValues?.count || 0
      let currentDirectMessagesLeft = 0
      const messageList = await getOptionalElement<HTMLElement>(
        document,
        '//div[@aria-label="Timeline: Messages"]',
      )

      if (messageList) {
        let nextMessageMoreMenus = await getAllVisibleDmMenus(messageList)

        remoteLog.debug(
          `twitterLeaveDirectMessages: Found ${nextMessageMoreMenus?.length} messages`,
        )

        while (nextMessageMoreMenus.length > 0) {
          for (const nextMessageMenu of nextMessageMoreMenus) {
            nextMessageMenu.click()
            await wait(500)

            const deleteConversation = await getElement<HTMLElement>(
              messageList,
              '//span[text()="Delete conversation"]',
            )
            deleteConversation.click()

            if (isDry) {
              // click Cancel
              const cancelButton = await getElement<HTMLElement>(
                document,
                '//span[text() = "Cancel"]',
              )
              cancelButton.click()
            } else {
              // Leave conversation
              const leaveButton = await getElement<HTMLElement>(
                document,
                '//span[text() = "Leave"]',
              )
              remoteLog.debug(
                'twitterLeaveDirectMessages: Clicking leave button',
                { element: leaveButton },
              )
              leaveButton.click()
              await wait(500)
            }
            currentDirectMessagesLeft += 1
            totalDirectMessagesLeft += 1

            await incrementTotalCount(
              TwitterRecommendationKey.TWITTER_LEAVE_DIRECT_MESSAGES,
            )

            await updateStoredInitialValues(
              {
                [XSettingKey.LEAVE_DIRECT_MESSAGES]: {
                  count: totalDirectMessagesLeft,
                  message: `You left ${totalDirectMessagesLeft} ${totalDirectMessagesLeft === 1 ? 'conversation' : 'conversations'} ✅`,
                },
              },
              TwitterRecommendationKey.TWITTER_LEAVE_DIRECT_MESSAGES,
            )

            await userContext.updateStatisticsLastCount(
              TwitterRecommendationKey.TWITTER_LEAVE_DIRECT_MESSAGES,
              currentDirectMessagesLeft,
            )
          }

          const emptyStateMessage = getOptionalElement<HTMLSpanElement>(
            document,
            '//span[text()="Welcome to your inbox!"]',
          )
          if (!emptyStateMessage) {
            remoteLog.debug('twitterLeaveDirectMessages: loading more dms')
            nextMessageMoreMenus = await getAllVisibleDmMenus(messageList)
          } else {
            remoteLog.debug(
              'twitterLeaveDirectMessages: empty inbox message found',
            )
            nextMessageMoreMenus = []
          }

          throwIfGlobalInterruptRequested()
        }
      } else {
        // No message list, no DMs
        updateStoredInitialValues(
          {
            [XSettingKey.LEAVE_DIRECT_MESSAGES]: {
              count: totalDirectMessagesLeft,
              message: `You left ${totalDirectMessagesLeft} ${totalDirectMessagesLeft === 1 ? 'conversation' : 'conversations'} ✅`,
              result: {
                title: 'No DMs found',
                detail: `${BuildConfig.TENANT_NAME} was not able to find any messages in your inbox.`,
              },
            },
          },
          TwitterRecommendationKey.TWITTER_LEAVE_DIRECT_MESSAGES,
        )
      }
    },
  },
]
